/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Form, Input, message } from 'antd';
import { Plus, X, Trash2, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { Button, Spinner } from '@crc-evans/ui-components';
import './DPR/DPRSettingsPipelinesTab.css';
import './AddPQTReportModal.css';

const { TextArea } = Input;

const DEFAULT_NAME = 'PQT Planning';

const RowElementSelect = ({
  value,
  options,
  onChange,
  disabled,
  isOpen,
  onOpenChange,
  dropdownFooter,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onOpenChange]);

  const selected = options.find((o) => o.value === value);
  const displayLabel = selected ? selected.label : '';

  return (
    <div ref={containerRef} className="dpr-settings-pipelines-select-wrap">
      <div
        className="dpr-settings-pipelines-select-trigger"
        onClick={() => !disabled && onOpenChange(!isOpen)}
        style={{
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span className="dpr-settings-pipelines-select-value">
          {displayLabel || 'Select activity'}
        </span>
        {isOpen ? <ChevronUp size={18} color="#6B7280" /> : <ChevronDown size={18} color="#6B7280" />}
      </div>
      {isOpen && (
        <div className="dpr-settings-pipelines-select-dropdown">
          {options.map((opt) => (
            <div
              key={opt.value}
              className="dpr-settings-pipelines-select-opt"
              onClick={() => {
                onChange(opt.value);
                onOpenChange(false);
              }}
            >
              {opt.label}
            </div>
          ))}
          {dropdownFooter}
        </div>
      )}
    </div>
  );
};

const AddPQTReportModal = (props) => {
  const {
    visible,
    onCancel,
    onSave,
    pqtElements,
    existingReport,
    loading,
    isReportFetching,
    isWriteAccess,
    onCreatePqtElement,
  } = props;

  const [form] = Form.useForm();
  const [rows, setRows] = useState([{ elementId: undefined, bidDays: 0 }]);
  const [addNewElementName, setAddNewElementName] = useState('');
  const [addingElement, setAddingElement] = useState(false);
  const [openSelectRow, setOpenSelectRow] = useState(null);

  const sortedElements = useMemo(() => {
    const list = Array.isArray(pqtElements) ? [...pqtElements] : [];
    return list.sort((a, b) =>
      String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' })
    );
  }, [pqtElements]);

  const selectOptions = useMemo(
    () => sortedElements.map((el) => ({ value: el.id, label: el.name })),
    [sortedElements]
  );

  useEffect(() => {
    if (visible && existingReport) {
      const report = existingReport;
      const hasReport =
        report &&
        (report.id != null ||
          report.projectPqtReportId != null ||
          (report.elements?.length || report.reportElements?.length));
      if (hasReport) {
        form.setFieldsValue({
          description: report.description ?? '',
        });
        const elements = report.elements ?? report.reportElements ?? [];
        const items = Array.isArray(elements) ? elements : [];
        if (items.length > 0) {
          setRows(
            items.map((el) => {
              const elemId = el.pqtElementId ?? el.pqtElement?.id ?? el.id;
              const bidDays = el.bidDays ?? el.bidNoOfDays ?? 0;
              return {
                elementId: elemId,
                bidDays: Number.isFinite(bidDays) ? bidDays : 0,
              };
            })
          );
        } else {
          setRows([{ elementId: undefined, bidDays: 0 }]);
        }
      } else {
        form.resetFields();
        setRows([{ elementId: undefined, bidDays: 0 }]);
      }
    } else if (visible && !existingReport) {
      form.resetFields();
      setRows([{ elementId: undefined, bidDays: 0 }]);
    }
    setOpenSelectRow(null);
  }, [visible, existingReport, form]);

  useEffect(() => {
    if (!visible) setOpenSelectRow(null);
  }, [visible]);

  const addRow = () => {
    setRows((prev) => [...prev, { elementId: undefined, bidDays: 0 }]);
  };

  const removeRow = (index) => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
    setOpenSelectRow((prev) => {
      if (prev === null) return null;
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const handleRowChange = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddNewElement = async () => {
    const name = (addNewElementName || '').trim();
    if (!name) return;
    setAddingElement(true);
    try {
      const maxOrder =
        sortedElements.length > 0
          ? Math.max(...sortedElements.map((e) => e.sortOrder ?? 0), 0) + 1
          : 1;
      if (onCreatePqtElement) {
        await onCreatePqtElement({ name, sortOrder: maxOrder });
      }
      setAddNewElementName('');
    } finally {
      setAddingElement(false);
    }
  };

  const clearNewElementInput = () => {
    setAddNewElementName('');
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const { description } = values;
        const elements = rows
          .filter((r) => r.elementId != null && r.elementId !== '')
          .map((r) => ({
            id: typeof r.elementId === 'number' ? r.elementId : parseInt(r.elementId, 10),
            bidDays: typeof r.bidDays === 'number' ? r.bidDays : parseInt(r.bidDays, 10) || 0,
          }));
        const name = existingReport?.name ?? DEFAULT_NAME;
        onSave({
          name,
          description: (description || '').trim(),
          elements,
          reportId:
            existingReport && (existingReport.id ?? existingReport.projectPqtReportId)
              ? existingReport.id ?? existingReport.projectPqtReportId
              : null,
        });
      })
      .catch(() => {
        message.warning('Please fill the required fields.');
      });
  };

  const blocked = !!isWriteAccess;

  if (!visible) {
    return null;
  }

  const addElementFooter =
    onCreatePqtElement && !blocked ? (
      <div className="pqt-report-modal-add-element">
        <input
          type="text"
          placeholder="New activity name"
          value={addNewElementName}
          onChange={(e) => setAddNewElementName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddNewElement();
            }
          }}
          disabled={addingElement}
        />
        <div className="pqt-report-modal-add-element-actions">
          <button
            type="button"
            title="Add"
            aria-label="Add element"
            onClick={handleAddNewElement}
            disabled={!(addNewElementName || '').trim() || addingElement}
          >
            <Check size={18} />
          </button>
          <button type="button" title="Clear" aria-label="Clear" onClick={clearNewElementInput}>
            <X size={18} />
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div
      className="dpr-settings-pipelines-modal-backdrop"
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="dpr-settings-pipelines-modal pqt-report-modal-shell"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pqt-report-modal-title"
      >
        <div className="dpr-settings-pipelines-modal-head">
          <h3 id="pqt-report-modal-title" className="dpr-settings-pipelines-modal-title">
            PQT Activities
          </h3>
          <button
            type="button"
            className="dpr-settings-pipelines-modal-close"
            onClick={onCancel}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div
          className="dpr-settings-pipelines-modal-body"
          style={{ maxHeight: 'min(70vh, calc(100vh - 220px))' }}
        >
          {isReportFetching ? (
            <div className="pqt-report-modal-loading">
              <Spinner size="medium" />
              <span>Loading…</span>
            </div>
          ) : (
            <Form form={form} layout="vertical" autoComplete="off" initialValues={{ description: '' }}>
              <div className="dpr-settings-pipelines-form-row">
                <div className="dpr-settings-pipelines-field">
                  <label htmlFor="pqt-report-description">
                    Description <span className="required">*</span>
                  </label>
                  <Form.Item
                    name="description"
                    rules={[{ required: true, message: 'Description is required' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <TextArea
                      id="pqt-report-description"
                      className="pqt-report-modal-textarea"
                      placeholder="Enter description"
                      disabled={blocked}
                      rows={5}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="pqt-report-modal-activities-toolbar">
                <div className="pqt-report-modal-col-activity">PQT Activities</div>
                <div className="pqt-report-modal-col-days">Bid No. of Days</div>
                <div className="pqt-report-modal-col-trash" aria-hidden />
                <div className="pqt-report-modal-col-add">
                  <Button
                    label="Add row"
                    variant="primary"
                    size="small"
                    icon={Plus}
                    onClick={addRow}
                    disabled={blocked}
                  />
                </div>
              </div>

              {rows.map((row, index) => (
                <div key={index} className="pqt-report-modal-row">
                  <div className="pqt-report-modal-col-activity">
                    <RowElementSelect
                      value={row.elementId}
                      options={selectOptions}
                      onChange={(v) => handleRowChange(index, 'elementId', v)}
                      disabled={blocked}
                      isOpen={openSelectRow === index}
                      onOpenChange={(open) => setOpenSelectRow(open ? index : null)}
                      dropdownFooter={addElementFooter}
                    />
                  </div>
                  <div className="pqt-report-modal-col-days">
                    <input
                      type="number"
                      min={0}
                      className="dpr-settings-pipelines-input"
                      placeholder="0"
                      value={row.bidDays}
                      onChange={(e) =>
                        handleRowChange(index, 'bidDays', parseInt(e.target.value, 10) || 0)
                      }
                      disabled={blocked}
                    />
                  </div>
                  <div className="pqt-report-modal-col-trash">
                    {rows.length > 1 && !blocked && (
                      <button
                        type="button"
                        className="pqt-report-modal-delete"
                        onClick={() => removeRow(index)}
                        aria-label="Delete row"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                  <div className="pqt-report-modal-col-add" />
                </div>
              ))}
            </Form>
          )}
        </div>

        <div className="dpr-settings-pipelines-modal-foot">
          <Button label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
          <Button
            label={
              existingReport && (existingReport.id ?? existingReport.projectPqtReportId)
                ? 'Update'
                : 'Add'
            }
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            loading={loading}
            disabled={blocked}
          />
        </div>
      </div>
    </div>
  );
};

export default AddPQTReportModal;
